import { request } from '@codametrix/client-api';
import { isCaseDetailTwoPhaseEnabled } from '@codametrix/client-runtime';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
/** True when a fetch was cancelled because a newer case-detail load took the abort key. */ function isAbortError(error) {
    return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
}
function parseXCacheHeader(response) {
    var _response_headers_get;
    const raw = (_response_headers_get = response.headers.get('X-Cache')) !== null && _response_headers_get !== void 0 ? _response_headers_get : response.headers.get('x-cache');
    if (!raw) {
        return undefined;
    }
    const normalized = raw.trim().toUpperCase();
    if (normalized === 'HIT' || normalized === 'MISS' || normalized === 'BYPASS') {
        return normalized;
    }
    return undefined;
}
function headersInitToRecord(headers) {
    if (!headers) {
        return {};
    }
    if (headers instanceof Headers) {
        const record = {};
        headers.forEach((value, key)=>{
            record[key] = value;
        });
        return record;
    }
    if (Array.isArray(headers)) {
        return Object.fromEntries(headers);
    }
    return _object_spread({}, headers);
}
function mergeCaseDetailCacheHeaders(init, options) {
    if ((options === null || options === void 0 ? void 0 : options.acceptCache) === undefined && (options === null || options === void 0 ? void 0 : options.cacheReplace) !== true) {
        return init;
    }
    // Plain object, not Headers: `@codametrix/client-api` spreads `init.headers`
    // into a record, which drops Headers instances (no enumerable own properties).
    const headers = _object_spread({}, headersInitToRecord(init.headers));
    if ((options === null || options === void 0 ? void 0 : options.acceptCache) !== undefined) {
        headers['Accept-Cache'] = options.acceptCache ? 'true' : 'false';
    }
    if (options === null || options === void 0 ? void 0 : options.cacheReplace) {
        headers['X-Cache-Replace'] = 'true';
    }
    return _object_spread_props(_object_spread({}, init), {
        headers
    });
}
/**
 * JSON fetch that preserves X-Cache for the gateway case-detail cache protocol.
 * Does not append refresh=true — that param is for XCaliber refresh only.
 */ async function requestJSONWithCacheMeta(input, init = {}, options) {
    const response = await request(input, mergeCaseDetailCacheHeaders(init, options));
    const xCache = parseXCacheHeader(response);
    if (response.status === 204) {
        return {
            data: null,
            xCache
        };
    }
    const data = await response.json();
    return {
        data,
        xCache
    };
}
function normalizeDiffContext(context) {
    if (context === undefined || context === null) {
        return {};
    }
    return typeof context === 'object' ? context : {
        processId: context
    };
}
/**
 * Meaningful active-CaseRun diff between cache (Phase 1) and fresh (Phase 2) payloads.
 * Paths with data-bearing dynamic keys are redacted for display/storage.
 * Side effect: when changes exist, logs a temporary diagnostic `console.group`.
 */ function caseDetailDiff(phase1, phase2, context) {
    const { processId, phase2UsedRefresh } = normalizeDiffContext(context);
    const changes = caseDetailChangedEntries(phase1, phase2, processId);
    const displayChanges = changes.map((change)=>_object_spread_props(_object_spread({}, change), {
            path: sanitizeDebugPath(change.path)
        }));
    if (changes.length > 0) {
        // Temporary: group left (Phase 1 / cache) vs right (Phase 2 / authoritative) for diagnosis.
        const annotations = [
            describeCacheStaleness(phase1, phase2, processId),
            phase2UsedRefresh === undefined ? undefined : `phase 2 refresh=${phase2UsedRefresh}`
        ].filter((annotation)=>annotation !== undefined);
        console.group(`[case-accelerator] Meaningful reconciliation differences (${changes.length}) — ${annotations.join('; ')}`);
        displayChanges.forEach(({ path, left, right })=>{
            console.debug(path, {
                left,
                right
            });
        });
        console.groupEnd();
    }
    return {
        differs: changes.length > 0,
        changes: displayChanges
    };
}
function caseDetailMeaningfulDataDiffers(phase1, phase2, context) {
    return caseDetailDiff(phase1, phase2, context).differs;
}
function formatStalenessDuration(milliseconds) {
    const totalMinutes = Math.round(milliseconds / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor(totalMinutes % 1440 / 60);
    const minutes = totalMinutes % 60;
    const parts = [
        ...days > 0 ? [
            `${days}d`
        ] : [],
        ...days > 0 || hours > 0 ? [
            `${hours}h`
        ] : [],
        `${minutes}m`
    ];
    return parts.join(' ');
}
/**
 * `last_entity_update` is excluded from drift detection, but it is the only direct measure of how
 * far behind the cached payload is — so it is surfaced alongside the diff instead.
 */ function describeCacheStaleness(phase1, phase2, processId) {
    var _comparableDateTime, _comparableDateTime1;
    const cached = readLastEntityUpdate(phase1, processId);
    const authoritative = readLastEntityUpdate(phase2, processId);
    const summary = `cache last_entity_update ${cached !== null && cached !== void 0 ? cached : 'absent'}, authoritative ${authoritative !== null && authoritative !== void 0 ? authoritative : 'absent'}`;
    const cachedInstant = (_comparableDateTime = comparableDateTime(cached)) === null || _comparableDateTime === void 0 ? void 0 : _comparableDateTime.instant;
    const authoritativeInstant = (_comparableDateTime1 = comparableDateTime(authoritative)) === null || _comparableDateTime1 === void 0 ? void 0 : _comparableDateTime1.instant;
    if (cachedInstant === undefined || authoritativeInstant === undefined) {
        return summary;
    }
    const drift = authoritativeInstant - cachedInstant;
    if (drift <= 0) {
        return `${summary} (cache not behind)`;
    }
    return `${summary} (cache ${formatStalenessDuration(drift)} behind)`;
}
/** @deprecated Use {@link caseDetailMeaningfulDataDiffers}. */ function caseDetailLastEntityUpdateDiffers(phase1, phase2, processId) {
    return caseDetailMeaningfulDataDiffers(phase1, phase2, processId);
}
const ignoredCaseRunPathPatterns = [
    /^last_entity_update$/,
    /^discharge_datetime$/,
    /(^|\.)admission_datetime$/,
    /(^|\.)medicare_consult_rules$/,
    /(^|\.)medicare_time_rules$/,
    /(^|\.)cmx_insurance_(plan_text|payer_id)$/,
    /(^|\.)cmx_mapped_payer_number$/,
    /(^|\.)hl7_insurance_/,
    /(^|\.)automation_[^.]+$/,
    /(^|\.)automate_decision_reason$/,
    /(^|\.)prediction_[^.]*_datetime$/,
    /(^|\.)send_to_coder_datetime$/,
    /(^|\.).*_prediction_process_reason$/,
    /(^|\.)process_case_indicator$/,
    /^invoices\.\d+\.(guid|custom_values|division)(\.|$)/,
    /^notes\.\d+\.document_id$/,
    /(^|\.)placer_order_number_id$/,
    /(^|\.)entering_provider_number$/,
    /^invoices\.\d+\.transactions\.\d+\.guid$/,
    /^facts(\.|$)/
];
function sanitizeDebugPath(path) {
    const segments = path.split('.');
    const firstDynamicSegment = segments.findIndex((segment)=>!/^(?:[A-Za-z_]\w*|\d+)$/.test(segment));
    if (firstDynamicSegment === -1) {
        return path;
    }
    return [
        ...segments.slice(0, firstDynamicSegment),
        '<dynamic-key>'
    ].join('.');
}
const timeZoneOffsets = {
    UTC: 0,
    EST: -5,
    EDT: -4,
    CST: -6,
    CDT: -5,
    MST: -7,
    MDT: -6,
    PST: -8,
    PDT: -7
};
function comparableDateTime(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const dateTimePattern = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2}) ([A-Z]{3})$/;
    const match = dateTimePattern.exec(value);
    if (!match) {
        return undefined;
    }
    const [, year, month, day, hour, minute, second, zone] = match;
    const offset = timeZoneOffsets[zone];
    const wallTime = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    const instant = offset === undefined ? undefined : Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour) - offset, Number(minute), Number(second));
    return {
        instant,
        wallTime
    };
}
function dateTimesAreEquivalent(left, right) {
    const leftDateTime = comparableDateTime(left);
    const rightDateTime = comparableDateTime(right);
    if (!leftDateTime || !rightDateTime) {
        return false;
    }
    return leftDateTime.wallTime === rightDateTime.wallTime || leftDateTime.instant !== undefined && leftDateTime.instant === rightDateTime.instant;
}
function isIgnoredCaseRunPath(path) {
    return ignoredCaseRunPathPatterns.some((pattern)=>pattern.test(path));
}
function collectChangedPaths(left, right, path, changedPaths) {
    if (isIgnoredCaseRunPath(path) || left === right || dateTimesAreEquivalent(left, right)) {
        return;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            changedPaths.push({
                path: `${path}.length`,
                left: left.length,
                right: right.length
            });
        }
        const sharedLength = Math.min(left.length, right.length);
        for(let index = 0; index < sharedLength; index += 1){
            collectChangedPaths(left[index], right[index], path ? `${path}.${index}` : `${index}`, changedPaths);
        }
        return;
    }
    if (left !== null && right !== null && typeof left === 'object' && typeof right === 'object') {
        const leftRecord = left;
        const rightRecord = right;
        const keys = Array.from(new Set([
            ...Object.keys(leftRecord),
            ...Object.keys(rightRecord)
        ])).sort((leftKey, rightKey)=>leftKey.localeCompare(rightKey));
        keys.forEach((key)=>{
            collectChangedPaths(leftRecord[key], rightRecord[key], path ? `${path}.${key}` : key, changedPaths);
        });
        return;
    }
    changedPaths.push({
        path: path || 'activeCaseRun',
        left,
        right
    });
}
function readActiveCaseRun(caseDetail, processId) {
    var _ref, _runs_find;
    const runs = (_ref = caseDetail === null || caseDetail === void 0 ? void 0 : caseDetail.caseRuns) !== null && _ref !== void 0 ? _ref : [];
    if (!runs.length) {
        return undefined;
    }
    if (processId !== undefined && processId !== null && `${processId}` !== '') {
        const match = runs.find((run)=>`${run.process_instance_id}` === `${processId}` || `${run.process_instance_guid}` === `${processId}`);
        if (match) {
            return match;
        }
    }
    // A supplied process id that matches nothing must still fall through to the primary run rather
    // than runs[0]; run ordering is not guaranteed to be stable between cache and origin.
    return (_runs_find = runs.find((run)=>run.primary_process_indicator === true)) !== null && _runs_find !== void 0 ? _runs_find : runs[0];
}
function identityPart(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}`;
    }
    if (typeof value === 'string' && value !== '') {
        return value;
    }
    return undefined;
}
/**
 * Stable identity for a CaseRun, used to compare like with like across payloads.
 * `undefined` when neither process identifier is present (payloads that predate them).
 */ function readCaseRunIdentity(run) {
    if (!run) {
        return undefined;
    }
    const id = identityPart(run.process_instance_id);
    const guid = identityPart(run.process_instance_guid);
    if (id === undefined && guid === undefined) {
        return undefined;
    }
    return [
        id !== null && id !== void 0 ? id : '-',
        guid !== null && guid !== void 0 ? guid : '-'
    ].join('/');
}
function isSameCaseRun(left, right) {
    const pairs = [
        [
            left.process_instance_id,
            right.process_instance_id
        ],
        [
            left.process_instance_guid,
            right.process_instance_guid
        ]
    ];
    return pairs.some(([leftValue, rightValue])=>{
        const leftPart = identityPart(leftValue);
        const rightPart = identityPart(rightValue);
        return leftPart !== undefined && leftPart === rightPart;
    });
}
function readCaseRunIdentities(caseDetail) {
    var _ref;
    return ((_ref = caseDetail === null || caseDetail === void 0 ? void 0 : caseDetail.caseRuns) !== null && _ref !== void 0 ? _ref : []).map(readCaseRunIdentity).filter((identity)=>identity !== undefined).sort((left, right)=>left.localeCompare(right));
}
/**
 * Pairs the displayed cached run with the same run in the authoritative payload by identity, so an
 * unstable `caseRuns` ordering cannot produce a field-by-field diff between two unrelated runs.
 * `aligned` is false when the cached run is absent from the authoritative payload.
 */ function readComparableCaseRuns(phase1, phase2, processId) {
    var _ref;
    const cached = readActiveCaseRun(phase1, processId);
    const fallback = readActiveCaseRun(phase2, processId);
    if (!cached || readCaseRunIdentity(cached) === undefined) {
        return {
            cached,
            authoritative: fallback,
            aligned: true
        };
    }
    const matched = ((_ref = phase2 === null || phase2 === void 0 ? void 0 : phase2.caseRuns) !== null && _ref !== void 0 ? _ref : []).find((run)=>isSameCaseRun(cached, run));
    return matched ? {
        cached,
        authoritative: matched,
        aligned: true
    } : {
        cached,
        authoritative: fallback,
        aligned: false
    };
}
/**
 * Returns meaningful active-CaseRun path/value pairs that changed between cache and
 * authoritative payloads. Reconciliation metadata, non-UI insurance representations,
 * `discharge_datetime`, generated transaction GUIDs, and the rule-engine `facts` bag
 * (not shown in case-detail UI) are ignored, and equivalent date-times formatted in UTC
 * versus a US timezone are treated as equal.
 *
 * A changed set of CaseRuns, or a displayed run missing from the authoritative payload, is reported
 * as a single identity difference rather than as drift on every field.
 */ function caseDetailChangedEntries(phase1, phase2, processId) {
    const changedPaths = [];
    const cachedIdentities = readCaseRunIdentities(phase1);
    const authoritativeIdentities = readCaseRunIdentities(phase2);
    if (cachedIdentities.length > 0 && authoritativeIdentities.length > 0 && cachedIdentities.join(',') !== authoritativeIdentities.join(',')) {
        changedPaths.push({
            path: 'caseRuns.identities',
            left: cachedIdentities,
            right: authoritativeIdentities
        });
    }
    const { cached, authoritative, aligned } = readComparableCaseRuns(phase1, phase2, processId);
    if (!aligned) {
        var _readCaseRunIdentity;
        changedPaths.push({
            path: 'activeCaseRun.identity',
            left: readCaseRunIdentity(cached),
            right: (_readCaseRunIdentity = readCaseRunIdentity(authoritative)) !== null && _readCaseRunIdentity !== void 0 ? _readCaseRunIdentity : null
        });
        return changedPaths;
    }
    collectChangedPaths(cached, authoritative, '', changedPaths);
    return changedPaths;
}
/**
 * Returns meaningful active-CaseRun paths that changed between cache and authoritative payloads.
 * Reconciliation metadata, non-UI insurance representations, `discharge_datetime`, generated
 * transaction GUIDs, and the `facts` bag are ignored, and equivalent date-times formatted in
 * UTC versus a US timezone are treated as equal.
 */ function caseDetailChangedPaths(phase1, phase2, processId) {
    return caseDetailChangedEntries(phase1, phase2, processId).map((entry)=>entry.path);
}
function readLastEntityUpdate(caseDetail, processId) {
    const activeRun = readActiveCaseRun(caseDetail, processId);
    if (!activeRun) {
        return null;
    }
    const value = activeRun.last_entity_update;
    return value !== null && value !== void 0 ? value : null;
}
/**
 * Appends `refresh=true` for Phase 2 on IPC / Bedside Pro so the reconcile fetch
 * also pulls fresh XCaliber data. Idempotent if the param is already present.
 */ function appendCaseDetailRefreshParam(url) {
    // The base (http://localhost) is needed because case-detail URLs are relative paths (/api/cases/...); 
    // URL requires an absolute URL to parse them. We return only pathname + search, so the base never appears in the result.
    const parsed = new URL(url, 'http://localhost');
    if (parsed.searchParams.get('refresh') === 'true') {
        return url;
    }
    parsed.searchParams.set('refresh', 'true');
    return `${parsed.pathname}${parsed.search}`;
}
function normalizeDiffersResult(result) {
    var _result_changes;
    if (typeof result === 'boolean') {
        return {
            differs: result,
            changes: []
        };
    }
    return {
        differs: result.differs,
        changes: (_result_changes = result.changes) !== null && _result_changes !== void 0 ? _result_changes : []
    };
}
/**
 * Shared two-phase orchestration for case-detail URLs.
 * Phase 2 runs only after Phase 1 `X-Cache: HIT`; uses `Accept-Cache: false`.
 * IPC / Bedside Pro Phase 2 URLs should include `refresh=true` (see {@link appendCaseDetailRefreshParam}).
 */ async function orchestrateTwoPhaseCaseDetailFetch(options) {
    var _options_enabled;
    const enabled = (_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : isCaseDetailTwoPhaseEnabled();
    if (!enabled) {
        var _options_single;
        const single = await ((_options_single = options.single) !== null && _options_single !== void 0 ? _options_single : options.phase1)();
        return {
            data: single.data,
            xCache: single.xCache,
            deferSupportingData: false
        };
    }
    try {
        var _options_onPhase1Complete;
        const phase1 = await options.phase1();
        await ((_options_onPhase1Complete = options.onPhase1Complete) === null || _options_onPhase1Complete === void 0 ? void 0 : _options_onPhase1Complete.call(options, phase1.data, phase1.xCache));
        if (phase1.xCache !== 'HIT') {
            return {
                data: phase1.data,
                xCache: phase1.xCache,
                deferSupportingData: false
            };
        }
        try {
            const phase2 = await options.phase2();
            const diff = normalizeDiffersResult(await options.differs(phase1.data, phase2.data));
            if (diff.differs) {
                // Auto-apply Phase 2. Phase 1 already painted via onPhase1Complete; callers replace
                // with `data`. Retain reconciliationDiff for a stacked comparison-modal PR.
                return {
                    data: phase2.data,
                    xCache: 'HIT',
                    deferSupportingData: false,
                    autoAppliedReconciliation: true,
                    reconciliationDiff: diff.changes
                };
            }
            return {
                data: phase2.data,
                xCache: 'HIT',
                deferSupportingData: false
            };
        } catch (error) {
            // A newer getDetail / loadFromURLMatch calls getSignal(DETAIL_RECONCILE), which
            // aborts this Phase 2. That is supersession, not a freshness failure — treating
            // it as a warning is what flashes "Unable to get the latest information" on the
            // first related-case open (mount + URL settle both start a load).
            if (isAbortError(error)) {
                throw error;
            }
            return {
                data: phase1.data,
                xCache: 'HIT',
                deferSupportingData: true,
                freshnessWarning: true
            };
        }
    } catch (error) {
        if (isAbortError(error)) {
            throw error;
        }
        const fallback = await options.fallback();
        return {
            data: fallback.data,
            xCache: fallback.xCache,
            deferSupportingData: false
        };
    }
}

export { appendCaseDetailRefreshParam as a, caseDetailChangedPaths as b, caseDetailDiff as c, caseDetailLastEntityUpdateDiffers as d, caseDetailMeaningfulDataDiffers as e, readLastEntityUpdate as f, isAbortError as i, orchestrateTwoPhaseCaseDetailFetch as o, parseXCacheHeader as p, requestJSONWithCacheMeta as r };
//# sourceMappingURL=case-accelerator-BXQZY-W-.mjs.map
